import { CSSProperties, FC, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Dropdown, Menu } from 'antd'
import ActionBlkBtn, { TActionBlkBtn } from './ActionBlkBtn'

const GROUP_ORDER = ['ALERTS', 'CAMPAIGNS', 'DATA']
const BTN_GAP = 8
const MORE_BTN_WIDTH = 90 + BTN_GAP
const ACTION_GROUP = 'action'
const MAX_ACTION_VISIBLE = 5
const INLINE_DROPDOWN_THRESHOLD = 4

const ActionBlkBar: FC<{
 btns: TActionBlkBtn[]
 style?: {
  wrapper?: CSSProperties
 }
}> = ({ btns, style }) => {
 // action-group buttons are toolbar candidates (max 5 visible)
 // everything else always goes to the More dropdown
 const actionBtns = useMemo(
  () => btns.filter((b) => b.group === ACTION_GROUP),
  [btns],
 )
 const nonActionBtns = useMemo(
  () => btns.filter((b) => b.group !== ACTION_GROUP),
  [btns],
 )

 const [dropdownStart, setDropdownStart] = useState(
  Math.min(actionBtns.length, MAX_ACTION_VISIBLE),
 )
 const containerRef = useRef<HTMLDivElement>(null)
 const measureRef = useRef<HTMLDivElement>(null)

 useEffect(() => {
  const calc = () => {
   const container = containerRef.current
   const measure = measureRef.current
   if (!container || !measure) return

   const available = container.clientWidth
   if (!available) return

   const btnEls = Array.from(measure.children) as HTMLElement[]
   if (btnEls.length !== actionBtns.length) return

   const maxVisible = Math.min(actionBtns.length, MAX_ACTION_VISIBLE)
   // More button is only reserved when non-action items exceed the inline threshold
   const alwaysHasMore = nonActionBtns.length > INLINE_DROPDOWN_THRESHOLD

   let total = 0
   let count = 0

   for (let i = 0; i < maxVisible; i++) {
    const w = btnEls[i].offsetWidth + (i > 0 ? BTN_GAP : 0)
    const isLastCandidate = i === maxVisible - 1
    // Reserve More button space unless last candidate with no non-action buttons
    const needsMoreSpace = !isLastCandidate || alwaysHasMore
    const spaceNeeded = total + w + (needsMoreSpace ? MORE_BTN_WIDTH : 0)

    if (spaceNeeded <= available || count === 0) {
     total += w
     count = i + 1
    } else {
     break
    }
   }

   setDropdownStart(count)
  }

  calc()
  const ro = new ResizeObserver(calc)
  if (containerRef.current) ro.observe(containerRef.current)
  return () => ro.disconnect()
 }, [btns]) // eslint-disable-line react-hooks/exhaustive-deps

 const visibleBtns = actionBtns.slice(0, dropdownStart)
 // Overflow action btns enter the dropdown as ungrouped items (strip group key)
 const overflowActionBtns = actionBtns
  .slice(dropdownStart)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  .map(({ group: _g, ...rest }) => rest as TActionBlkBtn)
 const dropdownBtns = [...overflowActionBtns, ...nonActionBtns]

 const buildMenuItems = (buttons: TActionBlkBtn[]) => {
  const ungrouped: TActionBlkBtn[] = []
  const grouped: Record<string, TActionBlkBtn[]> = {}

  buttons.forEach((btn) => {
   if (btn.group) {
    grouped[btn.group] = [...(grouped[btn.group] || []), btn]
   } else {
    ungrouped.push(btn)
   }
  })

  const items: any[] = ungrouped.map(({ icon, text, onClick, disabled }) => ({
   key: text,
   label: text,
   icon,
   disabled,
   onClick: (e: any) => onClick?.(e),
  }))

  const orderedGroups = [
   ...GROUP_ORDER.filter((g) => grouped[g]),
   ...Object.keys(grouped).filter((g) => !GROUP_ORDER.includes(g)),
  ]

  orderedGroups.forEach((groupName) => {
   items.push({
    type: 'group',
    label: groupName,
    children: grouped[groupName].map(({ icon, text, onClick, disabled }) => ({
     key: text,
     label: text,
     icon,
     disabled,
     onClick: (e: any) => onClick?.(e),
    })),
   })
  })

  return items
 }

 return (
  <div
   style={{
    flex: 1,
    minWidth: 0,
    padding: '0 20px',
    ...(style?.wrapper ?? {}),
   }}
  >
   {/* Off-screen: measure only action-group buttons (toolbar candidates) */}
   <div
    ref={measureRef}
    style={{
     position: 'fixed',
     top: -9999,
     left: -9999,
     display: 'flex',
     visibility: 'hidden',
     pointerEvents: 'none',
    }}
    aria-hidden="true"
   >
    {actionBtns.map((btn, i) => (
     <ActionBlkBtn {...btn} key={`m${i}`} />
    ))}
   </div>

   {/* Visible toolbar */}
   <div
    ref={containerRef}
    className="d-flex align-items-center gap-2"
    style={{
     width: '100%',
     justifyContent: 'end',
     overflow: 'hidden',
     minWidth: 0,
    }}
   >
    {visibleBtns.map((btn, i) => (
     <ActionBlkBtn {...btn} key={i} />
    ))}
    {dropdownBtns.length > INLINE_DROPDOWN_THRESHOLD && (
     <Dropdown overlay={<Menu items={buildMenuItems(dropdownBtns)} />}>
      <Button className="action-blk-more-btn">
       <i className="fas fa-ellipsis-h me-1" /> More
      </Button>
     </Dropdown>
    )}
    {dropdownBtns.length > 0 &&
     dropdownBtns.length <= INLINE_DROPDOWN_THRESHOLD &&
     dropdownBtns.map((btn, i) => <ActionBlkBtn {...btn} key={`d${i}`} />)}
   </div>
  </div>
 )
}

export default ActionBlkBar
